import './convert-funds.scss';

import { ReactComponent as BackButtonIcon } from '@basqet-dashboard/common/assets/icons/back-button.svg';
import { ReactComponent as ChevronRight } from '@basqet-dashboard/common/assets/icons/breadcrumb-arrow.svg';
import { getMaxDecimalPlaces } from '@basqet-dashboard/common/config';
import {
  useCreateConversionQuoteMutation,
  useCreateWalletConversionMutation
} from '@basqet-dashboard/common/hooks/mutations/walletConversions';
import { useFetchCollectionsWallets } from '@basqet-dashboard/common/hooks/queries';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { mapErrorToResult, mapQuoteErrorMessage, readError } from './convert-funds.errors';
import { EnterAmountStep, ProcessingStep, ResultStep, ReviewStep } from './steps';

const STEPS = {
  ENTER_AMOUNT: 'enter-amount',
  REVIEW: 'review',
  PROCESSING: 'processing',
  RESULT: 'result'
};

const STEP_LABELS = {
  [STEPS.ENTER_AMOUNT]: 'Step 1 of 3 — Enter amount',
  [STEPS.REVIEW]: 'Step 2 of 3 — Review',
  [STEPS.PROCESSING]: 'Step 3 of 3 — Processing',
  [STEPS.RESULT]: 'Done'
};

const stepLabel = (step) => STEP_LABELS[step] || '';

const QUOTE_TICK_MS = 1000;
const QUOTE_DEBOUNCE_MS = 350;
// Design caps the user-visible quote validity at 10s (figma node
// 13092:81286). Backend may serve a longer TTL — if so we honour the
// shorter of the two so the UI never claims more time than the user
// actually has.
const QUOTE_VISIBLE_TTL_MS = 10000;

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Insert thousands separators into the whole-number part of the value
// (matches the create-payment-link.jsx pattern). The displayed value
// stays human-friendly ("1,234.56") while the parsed numeric value used
// for math + the backend request strips the commas via parseAmount.
const formatWithCommas = (value) => {
  if (!value) return '';
  const [whole, frac] = String(value).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac !== undefined ? `${grouped}.${frac}` : grouped;
};

// Financial input hygiene: reject scientific notation, signs, currency
// symbols, multiple decimal points, and anything past maxDecimals.
// Backend rejects malformed values too, but normalising client-side
// avoids silently shipping precision the user didn't intend (e.g.
// "1e10" turning into 10,000,000,000) and stops fat-finger errors.
// Commas are inserted at the end for display; parseAmount strips them
// before any numeric use.
//
// maxDecimals comes from getMaxDecimalPlaces(fromSlug, toSlug) — mirrors
// the backend's per-market precision table so NGN→BTC can't even type
// a fractional NGN amount, etc.
const sanitizeAmountInput = (raw, { maxDecimals = 8 } = {}) => {
  let s = String(raw ?? '').replace(/[\s,]/g, '');
  if (!s) return '';
  s = s.replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot >= 0) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    const [whole, frac = ''] = s.split('.');
    if (maxDecimals <= 0) {
      // No decimals allowed for this market — strip the dot entirely.
      s = whole;
    } else {
      s = `${whole}.${frac.slice(0, maxDecimals)}`;
    }
  }
  return formatWithCommas(s);
};

// Strip commas + parse to Number for any math / submission. Commas in
// the displayed value never reach the backend.
const parseAmount = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Amount-only validation — runs on blur. Wallet-selection errors are
// intentionally NOT raised here; nagging the user with "select your
// destination wallet" while they're still typing the amount is bad UX.
// Cross-field errors fire on Next-click via validateForSubmit instead.
const validateAmountOnly = ({ amountInput, fromSlug, fromBalance }) => {
  const trimmed = String(amountInput ?? '').trim();
  if (!trimmed) return 'Enter an amount greater than 0';
  // Strict decimal pattern AFTER stripping commas — sanitizeAmountInput
  // produces "1,234.56" style; the canonical shape underneath is digits
  // with an optional single dot. Any drift means tampered/pasted input.
  const stripped = trimmed.replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(stripped)) return 'Enter a valid amount';
  const n = Number(stripped);
  if (!Number.isFinite(n) || n <= 0) return 'Enter an amount greater than 0';
  // Balance check only meaningful once source wallet is chosen.
  if (fromSlug && n > fromBalance) {
    return 'Your balance is insufficient to perform this transaction';
  }
  return '';
};

// Full submit-time validation — amount checks first, then the cross-
// field checks that block proceeding without both wallets selected.
const validateForSubmit = ({ amountInput, fromSlug, toSlug, fromBalance }) => {
  const amountError = validateAmountOnly({ amountInput, fromSlug, fromBalance });
  if (amountError) return amountError;
  if (!fromSlug || !toSlug) return 'Select source and destination wallets.';
  if (fromSlug === toSlug) return 'Source and destination must differ.';
  return '';
};

const generateReference = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `conv_${crypto.randomUUID()}`;
  }
  return `conv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

function ConvertFunds() {
  const history = useHistory();
  const { wallets } = useFetchCollectionsWallets();

  const [step, setStep] = useState(STEPS.ENTER_AMOUNT);
  const [result, setResult] = useState(null);
  const [fromSlug, setFromSlug] = useState('');
  const [toSlug, setToSlug] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [amountError, setAmountError] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteExpired, setQuoteExpired] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [reviewBannerExpired, setReviewBannerExpired] = useState(false);

  const quoteMutation = useCreateConversionQuoteMutation();
  const conversionMutation = useCreateWalletConversionMutation();

  const referenceRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fromWallet = wallets.find((wallet) => wallet?.currency?.slug === fromSlug);
  const fromBalance = safeNumber(fromWallet?.available_balance);
  // amountInput holds the comma-formatted display value. Numeric uses
  // (canRequestQuote, exceedsBalance, the quote payload) parse it back
  // to a number so commas never reach the backend.
  const numericAmount = parseAmount(amountInput);
  const hasValidAmount = numericAmount > 0;
  const exceedsBalance = hasValidAmount && numericAmount > fromBalance;
  const canRequestQuote = Boolean(
    fromSlug && toSlug && fromSlug !== toSlug && hasValidAmount && !exceedsBalance
  );

  const quoteRequestId = useRef(0);

  const requestQuote = useCallback(
    ({ silent, amountOverride } = {}) => {
      if (quoteMutation.isLoading) return;
      // amountOverride lets the caller (e.g. percent-preset click) pass
      // the just-set value rather than waiting for React to flush state.
      const amount = amountOverride ?? numericAmount;
      if (!fromSlug || !toSlug || fromSlug === toSlug) return;
      if (!(amount > 0) || amount > fromBalance) return;
      const id = ++quoteRequestId.current;
      quoteMutation.mutate(
        { from_currency: fromSlug, to_currency: toSlug, amount },
        {
          onSuccess: (response) => {
            if (!mountedRef.current || id !== quoteRequestId.current) return;
            setQuote(response?.data ?? null);
            setQuoteExpired(false);
            setReviewBannerExpired(false);
          },
          onError: (error) => {
            if (!mountedRef.current || id !== quoteRequestId.current) return;
            const { code, message } = readError(error);
            if (!silent) setQuote(null);
            setAmountError(mapQuoteErrorMessage(code, message));
          }
        }
      );
    },
    [fromSlug, toSlug, fromBalance, numericAmount, quoteMutation]
  );

  const numericAmountRef = useRef(numericAmount);
  numericAmountRef.current = numericAmount;
  const fromBalanceRef = useRef(fromBalance);
  fromBalanceRef.current = fromBalance;

  useEffect(() => {
    quoteRequestId.current += 1;
    if (!fromSlug || !toSlug || fromSlug === toSlug) {
      setQuote(null);
      setQuoteExpired(false);
      setReviewBannerExpired(false);
      return undefined;
    }
    setQuote(null);
    setQuoteExpired(false);
    setReviewBannerExpired(false);
    const amount = numericAmountRef.current;
    if (amount > 0 && amount <= fromBalanceRef.current) {
      const timer = setTimeout(() => requestQuote(), QUOTE_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [fromSlug, toSlug]);

  useEffect(() => {
    if (!quote?.expires_at) {
      setRemainingSeconds(0);
      return undefined;
    }
    const backendExpiresAt = new Date(quote.expires_at).getTime();
    const effectiveExpiresAt = Math.min(backendExpiresAt, Date.now() + QUOTE_VISIBLE_TTL_MS);
    const tick = () => {
      const ms = effectiveExpiresAt - Date.now();
      const seconds = Math.max(0, Math.ceil(ms / 1000));
      setRemainingSeconds(seconds);
      if (seconds === 0) setQuoteExpired(true);
    };
    tick();
    const interval = setInterval(tick, QUOTE_TICK_MS);
    return () => clearInterval(interval);
  }, [quote?.expires_at]);

  const resetQuoteState = () => {
    setQuote(null);
    setQuoteExpired(false);
    setReviewBannerExpired(false);
  };

  const goBack = () => {
    if (step === STEPS.ENTER_AMOUNT) {
      history.push('/conversions');
      return;
    }
    if (step === STEPS.REVIEW) {
      setStep(STEPS.ENTER_AMOUNT);
      return;
    }
    if (step === STEPS.RESULT) {
      resetQuoteState();
      setResult(null);
      setStep(STEPS.ENTER_AMOUNT);
    }
  };

  const handleSwap = () => {
    setFromSlug(toSlug);
    setToSlug(fromSlug);
    setAmountInput('');
    resetQuoteState();
    setAmountError('');
  };

  const handlePercent = (fraction) => {
    if (!fromWallet) return;
    // Round to the per-market cap (NGN→BTC = 0 dp, BTC→NGN = 5 dp etc.)
    // using FLOOR not toFixed — toFixed rounds half-to-even, so Max on
    // 0.37383738 with 5dp would produce 0.37384, which exceeds the
    // wallet balance and silently skips the quote fetch. Floor keeps us
    // safely under balance.
    const maxDp = getMaxDecimalPlaces(fromSlug, toSlug);
    const rawValue = fromBalance * fraction;
    const multiplier = Math.pow(10, Math.max(0, maxDp));
    const value = Math.floor(rawValue * multiplier) / multiplier;
    setAmountInput(formatWithCommas(String(value)));
    setAmountError('');

    if (toSlug && fromSlug !== toSlug && value > 0 && value <= fromBalance) {
      requestQuote({ amountOverride: value });
    }
  };

  const handleAmountBlur = () => {
    const error = validateAmountOnly({ amountInput, fromSlug, fromBalance });
    if (error) {
      setAmountError(error);
      return;
    }
    setAmountError('');
    if (canRequestQuote) requestQuote();
  };

  const validateOnNext = () => {
    const error = validateForSubmit({ amountInput, fromSlug, toSlug, fromBalance });
    if (error) {
      setAmountError(error);
      return false;
    }
    if (!quote) {
      setAmountError('Waiting for a quote — try again in a moment.');
      return false;
    }
    if (quoteExpired) return false;
    setAmountError('');
    return true;
  };

  const goToReview = () => {
    if (!validateOnNext()) return;
    setReviewBannerExpired(false);
    setStep(STEPS.REVIEW);
  };

  const confirmConversion = () => {
    if (!quote?.quote_id || conversionMutation.isLoading) return;
    if (quoteExpired) {
      setReviewBannerExpired(true);
      return;
    }
    if (!referenceRef.current) {
      referenceRef.current = generateReference();
    }
    setStep(STEPS.PROCESSING);
    const submittedQuote = quote;
    conversionMutation.mutate(
      { quote_id: quote.quote_id, reference: referenceRef.current },
      {
        onSuccess: (response) => {
          if (!mountedRef.current) return;
          referenceRef.current = null;
          setResult({
            variant: 'success',
            conversion: response?.data ?? null,
            quote: submittedQuote
          });
          setStep(STEPS.RESULT);
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          const { code, message } = readError(error);
          referenceRef.current = null;
          setResult(mapErrorToResult(code, message));
          setStep(STEPS.RESULT);
        }
      }
    );
  };

  const tryAgainFromResult = () => {
    resetQuoteState();
    setResult(null);
    setStep(STEPS.ENTER_AMOUNT);
    if (canRequestQuote) requestQuote();
  };

  const reviewExchangeRate = useMemo(() => {
    if (!quote) return '';
    const rate = safeNumber(quote.exchange_rate);
    return `1 ${quote.from_currency} = ${rate.toLocaleString('en-US', {
      maximumFractionDigits: 8
    })} ${quote.to_currency}`;
  }, [quote]);

  return (
    <div className="convert-funds">
      <div className="convert-funds__breadcrumb-bar">
        <nav className="convert-funds__breadcrumb" aria-label="Breadcrumb">
          <Link to="/conversions">Conversions</Link>
          <ChevronRight aria-hidden width={12} height={12} />
          <span>Convert funds</span>
        </nav>
      </div>

      <section className="convert-funds__card">
        <header className="convert-funds__card-header">
          <button
            type="button"
            className="convert-funds__back"
            aria-label="Go back"
            onClick={goBack}>
            <BackButtonIcon />
          </button>
          <div className="convert-funds__title-group">
            <h2 className="convert-funds__title">
              Convert <span className="text-cursive">funds</span>
            </h2>
            <p className="convert-funds__step-indicator">{stepLabel(step)}</p>
          </div>
        </header>

        {step === STEPS.ENTER_AMOUNT && (
          <EnterAmountStep
            wallets={wallets}
            fromSlug={fromSlug}
            toSlug={toSlug}
            setFromSlug={(slug) => {
              setFromSlug(slug);
              setAmountInput('');
              setAmountError('');
              resetQuoteState();
            }}
            setToSlug={(slug) => {
              setToSlug(slug);
              setAmountError('');
              resetQuoteState();
              // Re-sanitize the existing amount against the new pair's
              // decimal cap — switching to a stricter market (e.g. NGN
              // quote, dp = 0) should truncate any fractional bit
              // that's already in the input.
              setAmountInput((prev) =>
                sanitizeAmountInput(prev, { maxDecimals: getMaxDecimalPlaces(fromSlug, slug) })
              );
            }}
            amountInput={amountInput}
            setAmountInput={(value) => {
              setAmountInput(
                sanitizeAmountInput(value, {
                  maxDecimals: getMaxDecimalPlaces(fromSlug, toSlug)
                })
              );
              setAmountError('');
              // Any keystroke invalidates the existing quote (it was
              // priced for the prior amount). A new quote is fetched on
              // blur via handleAmountBlur, never on typing.
              setQuote(null);
              setQuoteExpired(false);
            }}
            fromWallet={fromWallet}
            onPercent={handlePercent}
            onSwap={handleSwap}
            isFetchingQuote={quoteMutation.isLoading}
            quote={quote}
            quoteExpired={quoteExpired}
            remainingSeconds={remainingSeconds}
            amountError={amountError}
            onAmountBlur={handleAmountBlur}
            onRequote={requestQuote}
            onNext={goToReview}
            nextEnabled={Boolean(
              quote && !quoteExpired && hasValidAmount && !exceedsBalance && !amountError
            )}
          />
        )}

        {step === STEPS.REVIEW && (
          <ReviewStep
            quote={quote}
            reviewExchangeRate={reviewExchangeRate}
            bannerExpired={reviewBannerExpired || quoteExpired}
            remainingSeconds={remainingSeconds}
            onRequote={requestQuote}
            onCancel={() => setStep(STEPS.ENTER_AMOUNT)}
            onConfirm={confirmConversion}
            isSubmitting={conversionMutation.isLoading}
          />
        )}

        {step === STEPS.PROCESSING && <ProcessingStep />}

        {step === STEPS.RESULT && result && (
          <ResultStep
            variant={result.variant}
            title={result.title}
            description={result.description}
            conversion={result.conversion}
            quote={result.quote}
            onTryAgain={tryAgainFromResult}
            onCancel={() => history.push('/conversions')}
            onDone={() => history.push('/conversions')}
          />
        )}
      </section>
    </div>
  );
}

export { ConvertFunds };
